import { useMemo } from 'react';
import type React from 'react';
import type { MouseEventHandler } from 'react';
import {
  activateBySpaceOrEnterButton,
  activateByEnterButton,
} from '../../../../utils/a11y';
import { isValidLink } from '../../../../common/Link/Link';
import { useAnalyticsReportClicks } from '../../../../utils/hooks/useAnalyticsReportClicks';
import type { ButtonLinkProps } from '../Button.types';

type UseButtonEventHandlersParams = {
  isDisabled: boolean;
  link?: ButtonLinkProps;
  propsOnClick?: () => void;
  preventLinkNavigation?: boolean;
  reportBiOnClick?: React.MouseEventHandler;
  onMouseEnter: MouseEventHandler;
  onMouseLeave: MouseEventHandler;
  onIconAnimationClick?: MouseEventHandler;
  onDblClick?: React.MouseEventHandler;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
};

export function useButtonEventHandlers({
  isDisabled,
  link,
  propsOnClick,
  preventLinkNavigation,
  reportBiOnClick,
  onMouseEnter,
  onMouseLeave,
  onIconAnimationClick,
  onDblClick,
  onFocus,
  onBlur,
}: UseButtonEventHandlersParams) {
  const isLink = isValidLink(link as any);
  const shouldPreventLinkNavigation = preventLinkNavigation && isLink;
  const shouldHaveOnClick =
    (!isDisabled && propsOnClick) || shouldPreventLinkNavigation;

  const onClick = useAnalyticsReportClicks({
    reportBiOnClick,
    onClick:
      shouldHaveOnClick || onIconAnimationClick
        ? (event: React.MouseEvent) => {
            onIconAnimationClick?.(event);
            if (shouldHaveOnClick) {
              shouldPreventLinkNavigation && event.preventDefault();
              !isDisabled && propsOnClick?.();
            }
          }
        : undefined,
  });

  const eventHandlers = useMemo(
    () => ({
      onClick,
      onMouseEnter,
      onMouseLeave,
      onKeyDown: isLink ? activateByEnterButton : activateBySpaceOrEnterButton,
      onDoubleClick: !isDisabled && onDblClick ? onDblClick : undefined,
      onFocus: !isDisabled && onFocus ? onFocus : undefined,
      onBlur: !isDisabled && onBlur ? onBlur : undefined,
    }),
    [
      onClick,
      onMouseEnter,
      onMouseLeave,
      isLink,
      isDisabled,
      onDblClick,
      onFocus,
      onBlur,
    ],
  );

  return { isLink, eventHandlers };
}
